import React, { useState } from 'react';
import classnames from 'classnames';
import { MixAndMatchPickerRow } from 'src/MixAndMatchBundle/MixAndMatchPickerRow';
import { CamelizedMediaEntry } from 'src/types/global-types';
import { MixAndMatchVariant } from 'src/MixAndMatchBundle/MixAndMatchProductContainer';
import { camelized_BundleConfigOptions_configurable_options_values } from 'src/queries/__fragments__/__generated__/BundleConfigOptions';
import { FormikValuesBundlesType } from 'src/MixAndMatchBundle/MixAndMatchBundle';
import { WearerOptionMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';

import classes from './MixAndMatchProductPicker.scss';

export type MixAndMatchValueType = {
    colors: camelized_BundleConfigOptions_configurable_options_values[] | undefined;
    sizes: camelized_BundleConfigOptions_configurable_options_values[] | undefined;
};

export type HandleBundleColorChangeType = (
    swatch: camelized_BundleConfigOptions_configurable_options_values,
    index: number,
    productIndex: number,
    productToSwapColors?: number,
) => void;

export type HandleBundleSizeChangeType = (
    size: { id: number; quantity: number },
    index: number,
    productIndex: number,
) => void;

type Props = {
    handleAddProductToBundle: (index: number, id: number, name: string) => void;
    handleRemoveProductFromBundle: (index: number, productIndex: number) => void;
    handleBundleColorChange: HandleBundleColorChangeType;
    handleBundleSizeChange: HandleBundleSizeChangeType;
    handleBundleQuantityChange: (quantity: number, index: number, productIndex: number) => void;
    variants: MixAndMatchVariant[];
    index: number;
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
    bundles: FormikValuesBundlesType[];
};

const getProductIndex = (chosenProducts, id) => chosenProducts.map((product) => product.id).indexOf(id);

export const MixAndMatchProductPicker: React.FC<Props> = ({
    handleAddProductToBundle,
    handleRemoveProductFromBundle,
    handleBundleColorChange,
    handleBundleSizeChange,
    handleBundleQuantityChange,
    variants,
    index,
    wearerOptions,
    summary,
    bundles,
}) => {
    const [chosenProducts, setChosenProducts] = useState<
        {
            id: number;
            productId?: number;
            name: string;
            value: MixAndMatchValueType | undefined;
            image?: string;
            mediaEntries?: CamelizedMediaEntry[];
            colorId?: number;
            maxQty?: number;
        }[]
    >([
        {
            id: 0,
            productId: undefined,
            name: '',
            value: undefined,
            image: undefined,
            mediaEntries: undefined,
            colorId: undefined,
            maxQty: undefined,
        },
    ]);

    const removeProduct = (idToRemove) => {
        const updatedProducts = chosenProducts.filter((product) => product.id !== idToRemove);
        setChosenProducts(updatedProducts);

        handleRemoveProductFromBundle(index, getProductIndex(chosenProducts, idToRemove));
    };

    const handleProductChange = (
        id: number,
        productId: number,
        name: string,
        newValue: MixAndMatchValueType,
        isProductPicked: boolean,
        image?: string,
        mediaEntries?: CamelizedMediaEntry[],
    ) => {
        const updatedProducts = chosenProducts.map((product) => {
            if (product.id === id) {
                return { id, productId, name, value: newValue, image, mediaEntries };
            } else return product;
        });

        if (!isProductPicked) {
            setChosenProducts([
                ...updatedProducts,
                {
                    id: id + 1,
                    productId,
                    name: '',
                    value: undefined,
                    image: undefined,
                    mediaEntries: undefined,
                },
            ]);
            handleAddProductToBundle(index, id, name);
        }
        if (isProductPicked) {
            setChosenProducts(updatedProducts);
            handleRemoveProductFromBundle(index, getProductIndex(chosenProducts, id));
            handleAddProductToBundle(index, id, name);
        }
    };

    const isRowDisabled = (productIndex: number) => {
        const productAlreadyPicked = Boolean(bundles[index].products[productIndex]?.name);

        if (chosenProducts.length === 1 || productIndex === 0 || productAlreadyPicked) return false;

        const previousBundleColorsAndSizes = bundles[index].products[productIndex - 1]?.selectedColorsAndSizes;

        if (previousBundleColorsAndSizes) {
            for (const color of previousBundleColorsAndSizes) {
                if (color.colorId && color.sizes.id && color.sizes.quantity) return false;
            }
        }

        return true;
    };

    const handleProductValueChange = (productId: number, value: number, type: 'colorId' | 'maxQty') => {
        const currentProductIndex = chosenProducts.findIndex((product) => product.id === productId);
        const chosenProductsMapped = [...chosenProducts];
        chosenProductsMapped[currentProductIndex][type] = value;
        setChosenProducts(chosenProductsMapped);
    };

    return (
        <div className={classes.tableContainer}>
            <div className={classnames(classes.table, classes.tableHeader)}>
                <span />
                <span />
                <span>Product</span>
                <span>Colour</span>
                <span>Size</span>
                <span>Amount</span>
            </div>
            {chosenProducts.map((product) => {
                const productIndex = getProductIndex(chosenProducts, product.id);

                return (
                    <MixAndMatchPickerRow
                        disabled={isRowDisabled(productIndex)}
                        key={product.id}
                        id={product.id}
                        productId={product.productId}
                        productName={product.name}
                        productIndex={getProductIndex(chosenProducts, product.id)}
                        productImage={product.image}
                        productMedia={product.mediaEntries}
                        value={product.value}
                        onDelete={removeProduct}
                        handleBundleColorChange={handleBundleColorChange}
                        handleBundleSizeChange={handleBundleSizeChange}
                        handleProductChange={handleProductChange}
                        handleProductValueChange={handleProductValueChange}
                        index={index}
                        handleBundleQuantityChange={handleBundleQuantityChange}
                        variants={variants}
                        wearerOptions={wearerOptions}
                        summary={summary}
                        colorId={product?.colorId}
                        maxQty={product?.maxQty}
                    />
                );
            })}
        </div>
    );
};
